import React, { useState } from 'react';
import { theme } from '../../theme';
import { TEAM_COLORS, areTeamColorsEqual, getTeamColorName } from '../../utils/teamColors';

interface Props {
  label?: string;
  selectedColor: string;
  onColorSelect: (hex: string) => void;
  disabledColor?: string;
  /** `inline`: solo el círculo (para meterlo dentro de una fila junto al nombre del equipo) */
  variant?: 'default' | 'inline';
}

const isLightSwatch = (hex: string) =>
  areTeamColorsEqual(hex, '#FFFFFF') || areTeamColorsEqual(hex, '#FFC107');

export const TeamColorPicker: React.FC<Props> = ({
  label,
  selectedColor,
  onColorSelect,
  disabledColor,
  variant = 'default',
}) => {
  const [open, setOpen] = useState(false);
  const name = getTeamColorName(selectedColor);

  const handlePick = (hex: string) => {
    if (disabledColor && areTeamColorsEqual(disabledColor, hex)) return;
    onColorSelect(hex);
    setOpen(false);
  };

  const swatch = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-label={`Color ${name}. Click para cambiar`}
      style={{
        position: 'relative',
        width: 44,
        height: 44,
        borderRadius: 22,
        background: selectedColor,
        border: `2px solid ${
          isLightSwatch(selectedColor) ? theme.colors.border : theme.colors.background
        }`,
        cursor: 'pointer',
        padding: 0,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          right: -2,
          bottom: -2,
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: theme.colors.surface,
          border: `1px solid ${theme.colors.border}`,
          fontSize: 11,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        🎨
      </span>
    </button>
  );

  return (
    <>
      {variant === 'inline' ? (
        swatch
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {label && (
            <span style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>{label}</span>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '8px 10px',
              borderRadius: 10,
              border: `1px solid ${theme.colors.border}`,
              background: theme.colors.background,
            }}
          >
            {swatch}
            <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: theme.colors.text }}>
              {name}
            </span>
            <span style={{ color: theme.colors.textSecondary, fontSize: 18 }}>›</span>
          </div>
        </div>
      )}

      {open && (
        <div
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: theme.colors.surface,
              borderRadius: 16,
              border: `1px solid ${theme.colors.border}`,
              padding: 20,
              maxWidth: 380,
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: theme.colors.text }}>
                {label || 'Color del equipo'}
              </h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Cerrar"
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 22,
                  cursor: 'pointer',
                  color: theme.colors.textSecondary,
                  padding: '4px 8px',
                }}
              >
                ×
              </button>
            </div>
            <p
              style={{
                margin: '0 0 16px',
                fontSize: 12,
                color: theme.colors.textSecondary,
              }}
            >
              El otro equipo usa un color distinto; si coincidís, ajustamos automáticamente.
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: 10,
                justifyItems: 'center',
              }}
            >
              {TEAM_COLORS.map((color) => {
                const isSelected = areTeamColorsEqual(selectedColor, color.hex);
                const isDisabled = !!disabledColor && areTeamColorsEqual(disabledColor, color.hex);
                const tickColor = areTeamColorsEqual(color.hex, '#FFFFFF') ? '#000' : '#fff';
                return (
                  <button
                    key={color.hex}
                    type="button"
                    onClick={() => handlePick(color.hex)}
                    disabled={isDisabled}
                    aria-label={`${color.name}${isDisabled ? ', no disponible' : ''}`}
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: 25,
                      background: color.hex,
                      border: `3px solid ${
                        isSelected ? theme.colors.primary : theme.colors.border
                      }`,
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      opacity: isDisabled ? 0.35 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                      position: 'relative',
                    }}
                  >
                    {isSelected && (
                      <span style={{ color: tickColor, fontSize: 18, fontWeight: 700 }}>✓</span>
                    )}
                    {isDisabled && (
                      <span
                        style={{
                          color: tickColor,
                          fontSize: 14,
                          fontWeight: 700,
                          opacity: 0.6,
                        }}
                      >
                        ✕
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TeamColorPicker;
